import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { API_BASE_URL } from "../../environment";
import { AuthContext } from "../../context/authcontext";
import {
  clearFilterState,
  getStoredFilterState,
  setFilterState,
  StoredFilterState,
} from "../../utils/filterStateUtils";

const spinStyle = `
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const dateRangeStyle = `
  .icon-form .table-filters-daterange.bookingrange {
    padding-left: 34px !important;
  }
  
  .icon-form .table-filters-daterange.filter-active-input {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
  
  .icon-form.filter-active .table-filters-daterange {
    border-color: #007bff;
  }
  
 
  .daterangepicker {
    z-index: 1050 !important;
  }
  
 
  .table-filters-daterange.bookingrange[readonly] {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
`;

if (
  typeof document !== "undefined" &&
  !document.head.querySelector("style[data-table-filters-spin]")
) {
  const styleElement = document.createElement("style");
  styleElement.textContent = spinStyle;
  styleElement.setAttribute("data-table-filters-spin", "true");
  document.head.appendChild(styleElement);
}

if (
  typeof document !== "undefined" &&
  !document.head.querySelector("style[data-table-filters-daterange]")
) {
  const styleElement = document.createElement("style");
  styleElement.textContent = dateRangeStyle;
  styleElement.setAttribute("data-table-filters-daterange", "true");
  document.head.appendChild(styleElement);
}

export type SortByOption = "newest" | "oldest";

const appendSortByParam = (
  params: URLSearchParams,
  sortBy?: SortByOption | null
) => {
  if (sortBy) {
    params.set("sort_by", sortBy);
  }
};

export interface FilterState {
  searchTerm: string;
  dateRange: [Date | null, Date | null];
  industryFilter: string[];
  countryFilter: string[];
  statusFilter: number | boolean | null;
  sortBy: string;
  sortOrder: "asc" | "desc";
  archivedFilter?: string;
  sort_by?: SortByOption | null;
}

export const mergeTableFilterChanges = <T extends FilterState>(
  current: T,
  next: FilterState
): T => {
  const sortChanged = !!next.sort_by || next.sort_by !== current.sort_by;
  return {
    ...current,
    searchTerm: next.searchTerm,
    dateRange: next.dateRange,
    sort_by: next.sort_by,
    ...(sortChanged ? { sortBy: next.sortBy, sortOrder: next.sortOrder } : {}),
  };
};

export interface FilterConfig {
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchField?: string;
  showDateFilter?: boolean;
  dateField?: string;
  showIndustryFilter?: boolean;
  industryField?: string;
  showCountryFilter?: boolean;
  countryField?: string;
  showStatusFilter?: boolean;
  additionalFilters?: AdditionalFilter[];
  defaultSort?: {
    field: string;
    order: "asc" | "desc";
  };
  pageName?:
    | "company"
    | "contact"
    | "deal"
    | "lead"
    | "pipeline"
    | "subscription"
    | "proposal"
    | "contract";
}

export interface AdditionalFilter {
  key: string;
  label: string;
  options: string[];
  field: string;
}

interface TableFiltersProps {
  config: FilterConfig;
  data: any[];
  onFilterChange: (filters: FilterState) => void;
  onApiFilterChange?: (apiFilters: Record<string, any>) => void;
  onApiCall?: (url: string) => Promise<void>;
  onFiltersReset?: () => void;
  tenantId?: string;
  className?: string;
  sortBy?: SortByOption | null;
  searchTerm?: string;
  dateRange?: [Date | null, Date | null];
  hideClearButton?: boolean;
}

interface LifecycleStage {
  lifecycle_type_stage_id: number;
  stage_name: string;
  stage_order: number;
  tenant_id: number;
  lifecycle_type_id: number;
}

const TableFilters: React.FC<TableFiltersProps> = React.memo(
  ({
    config,
    data,
    onFilterChange,
    onApiFilterChange,
    onApiCall,
    onFiltersReset,
    tenantId,
    className = "",
    sortBy,
    searchTerm,
    dateRange,
    hideClearButton = false,
  }) => {
    const navigate = useNavigate();
    const { lifecycleTypeNames } = useContext(AuthContext);

    const [displayNames, setDisplayNames] = useState({
      Lead: "Lead",
      Pipeline: "Pipeline",
      Deal: "Deal",
      Proposal: "Proposal",
      Contract: "Contract",
    });

    const [lifecycleStages, setLifecycleStages] = useState<LifecycleStage[]>(
      []
    );
    const [isLoadingStages, setIsLoadingStages] = useState(false);

    const statusDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (lifecycleTypeNames && Object.keys(lifecycleTypeNames).length > 0) {
        setDisplayNames({
          Lead: lifecycleTypeNames["LEAD"] || "Lead",
          Pipeline: lifecycleTypeNames["PIPELINE"] || "Pipeline",
          Deal: lifecycleTypeNames["DEAL"] || "Deal",
          Proposal: lifecycleTypeNames["PROPOSAL"] || "Proposal",
          Contract: lifecycleTypeNames["CONTRACT"] || "Contract",
        });
      } else {
        try {
          const storedNames = JSON.parse(
            localStorage.getItem("lifecycleTypeNames") || "{}"
          );
          if (Object.keys(storedNames).length > 0) {
            setDisplayNames({
              Lead: storedNames["LEAD"] || "Lead",
              Pipeline: storedNames["PIPELINE"] || "Pipeline",
              Deal: storedNames["DEAL"] || "Deal",
              Proposal: storedNames["PROPOSAL"] || "Proposal",
              Contract: storedNames["CONTRACT"] || "Contract",
            });
          }
        } catch (error) {
          console.error(
            "Error loading lifecycle names from localStorage:",
            error
          );
        }
      }
    }, [lifecycleTypeNames]);

    useEffect(() => {
      const handleStorageChange = (event: StorageEvent) => {
        if (event.key === "lifecycleTypeNames" && event.newValue) {
          try {
            const updatedNames = JSON.parse(event.newValue);
            setDisplayNames({
              Lead: updatedNames["LEAD"] || "Lead",
              Pipeline: updatedNames["PIPELINE"] || "Pipeline",
              Deal: updatedNames["DEAL"] || "Deal",
              Proposal: updatedNames["PROPOSAL"] || "Proposal",
              Contract: updatedNames["CONTRACT"] || "Contract",
            });
          } catch (error) {
            console.error(
              "Error parsing lifecycleTypeNames from localStorage:",
              error
            );
          }
        }
      };

      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    }, []);

    const handleApiError = (error: any, context: string) => {
      console.error(`${context} API error:`, error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("authCred");
        localStorage.removeItem("authResponse");
        localStorage.removeItem("authTimestamp");
        localStorage.removeItem("selectedTenantId");
        localStorage.removeItem("selectedTenantId");
        navigate("/login");
      }
    };

    const [filters, setFilters] = useState<FilterState>(() => {
      if (config.pageName) {
        const savedState = getStoredFilterState(config.pageName);
        if (savedState) {
          const dateRange: [Date | null, Date | null] = [
            savedState.dateRange[0] ? new Date(savedState.dateRange[0]) : null,
            savedState.dateRange[1] ? new Date(savedState.dateRange[1]) : null,
          ];

          return {
            searchTerm: "",
            dateRange,
            industryFilter: [],
            countryFilter: [],
            statusFilter: savedState.statusFilter,
            sortBy: config.defaultSort?.field || "",
            sortOrder: config.defaultSort?.order || "desc",
          };
        }
      }

      return {
        searchTerm: "",
        dateRange: [null, null] as [Date | null, Date | null],
        industryFilter: [],
        countryFilter: [],
        statusFilter: null,
        sortBy: config.defaultSort?.field || "",
        sortOrder: config.defaultSort?.order || "desc",
      };
    });

    const [selectedRangeLabel, setSelectedRangeLabel] = useState<string | null>(
      () => {
        if (config.pageName) {
          const savedState = getStoredFilterState(config.pageName);
          return savedState?.selectedRange || null;
        }
        return null;
      }
    );

    const [isSearching, setIsSearching] = useState(false);

    const initialSettings = {
      endDate: new Date(),
      ranges: {
        "Last 30 Days": [
          new Date(new Date().setDate(new Date().getDate() - 30)),
          new Date(),
        ],
        "Last 7 Days": [
          new Date(new Date().setDate(new Date().getDate() - 7)),
          new Date(),
        ],
        "Last Month": [
          new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
          new Date(new Date().getFullYear(), new Date().getMonth(), 0),
        ],
        "This Month": [
          new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          new Date(),
        ],
        Today: [new Date(), new Date()],
        Yesterday: [
          new Date(new Date().setDate(new Date().getDate() - 1)),
          new Date(new Date().setDate(new Date().getDate() - 1)),
        ],
        "This Year": (() => {
          const today = new Date();
          return [new Date(today.getFullYear(), 0, 1), today];
        })(),
        "Last Year": (() => {
          const today = new Date();
          const lastYear = today.getFullYear() - 1;
          return [new Date(lastYear, 0, 1), new Date(lastYear, 11, 31)];
        })(),
        "This Quarter": (() => {
          const today = new Date();
          const currentQuarter = Math.floor(today.getMonth() / 3);
          const quarterStartMonth = currentQuarter * 3;
          return [new Date(today.getFullYear(), quarterStartMonth, 1), today];
        })(),
        "Last Quarter": (() => {
          const today = new Date();
          const currentQuarter = Math.floor(today.getMonth() / 3);
          const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
          const lastQuarterYear =
            currentQuarter === 0
              ? today.getFullYear() - 1
              : today.getFullYear();
          const quarterStartMonth = lastQuarter * 3;
          const quarterEndMonth = quarterStartMonth + 2;
          const lastDayOfMonth = new Date(
            lastQuarterYear,
            quarterEndMonth + 1,
            0
          ).getDate();
          return [
            new Date(lastQuarterYear, quarterStartMonth, 1),
            new Date(lastQuarterYear, quarterEndMonth, lastDayOfMonth),
          ];
        })(),
      },
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      timePicker: false,
    };

    const uniqueIndustries = config.showIndustryFilter
      ? Array.from(
          new Set(
            data
              .map(
                (item) =>
                  item[config.industryField || "industry_type_display_name"]
              )
              .filter(Boolean)
          )
        )
      : [];

    const uniqueCountries = config.showCountryFilter
      ? Array.from(
          new Set(
            data
              .map((item) => item[config.countryField || "country"])
              .filter(Boolean)
          )
        )
      : [];

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const fetchLifecycleStages = useCallback(async () => {
      if (
        !config.showStatusFilter ||
        !config.pageName ||
        !tenantId ||
        config.pageName === "subscription"
      )
        return;

      setIsLoadingStages(true);
      try {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return;

        const lifecycleTypeKey = config.pageName.toUpperCase();
        const lifecycleTypeResponse = await fetch(
          `${API_BASE_URL}/lifecycle-type/?lifecycle_type_key=${lifecycleTypeKey}&tenant_id=${tenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!lifecycleTypeResponse.ok) {
          throw new Error("Failed to fetch lifecycle type");
        }

        const lifecycleTypeData = await lifecycleTypeResponse.json();
        const lifecycleType =
          lifecycleTypeData.results?.[0] || lifecycleTypeData[0];

        if (!lifecycleType) {
          console.warn(`No lifecycle type found for ${lifecycleTypeKey}`);
          return;
        }

        const stagesResponse = await fetch(
          `${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${lifecycleType.lifecycle_type_id}&tenant_id=${tenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!stagesResponse.ok) {
          throw new Error("Failed to fetch lifecycle stages");
        }

        const stagesData = await stagesResponse.json();
        const stages = Array.isArray(stagesData) ? stagesData : [];

        const sortedStages = stages.sort(
          (a: LifecycleStage, b: LifecycleStage) =>
            a.stage_order - b.stage_order
        );
        setLifecycleStages(sortedStages);
      } catch (error) {
        console.error("Error fetching lifecycle stages:", error);
        handleApiError(error, "Lifecycle stages");
      } finally {
        setIsLoadingStages(false);
      }
    }, [config.showStatusFilter, config.pageName, tenantId]);

    useEffect(() => {
      fetchLifecycleStages();
    }, [fetchLifecycleStages]);

    useEffect(() => {
      if (
        config.pageName &&
        (filters.dateRange[0] ||
          filters.dateRange[1] ||
          filters.statusFilter !== null)
      ) {
        const filterState: StoredFilterState = {
          dateRange: [
            filters.dateRange[0] ? filters.dateRange[0].toISOString() : null,
            filters.dateRange[1] ? filters.dateRange[1].toISOString() : null,
          ],
          statusFilter: filters.statusFilter,
          selectedRange: selectedRangeLabel || undefined,
        };
        setFilterState(config.pageName, filterState);
      }
    }, [
      filters.dateRange,
      filters.statusFilter,
      config.pageName,
      selectedRangeLabel,
    ]);

    useEffect(() => {
      const hasDateFilter = filters.dateRange[0] || filters.dateRange[1];
      const hasStatusFilter = filters.statusFilter !== null;

      if (config.pageName && (hasDateFilter || hasStatusFilter)) {
        onFilterChange(filters);

        if (onApiCall && (config.pageName === "subscription" || tenantId)) {
          makeApiCall();
        }
      }
    }, []);

    const generateApiUrl = (
      currentFilters: FilterState = filters,
      pageSize?: number
    ): string | null => {
      if (!config.pageName) return null;
      if (config.pageName !== "subscription" && !tenantId) return null;

      const baseEndpoints = {
        company: "/company/",
        contact: "/contact/",
        deal: "/deal/",
        lead: "/lead/",
        pipeline: "/pipeline/",
        subscription: "/tenant-subscription/",
        proposal: "/proposal/",
        contract: "/contract/",
      };

      const searchFields = {
        company: "company_name",
        contact: "contact_full_name",
        deal: "title",
        lead: "title",
        pipeline: "title",
        subscription: "tenant",
        proposal: "subject",
        contract: "subject",
      };

      const endpoint = baseEndpoints[config.pageName];
      if (!endpoint) return null;

      const params = new URLSearchParams();
      if (config.pageName !== "subscription" && tenantId) {
        params.set("tenant_id", tenantId);
      }

      if (["lead", "pipeline", "deal"].includes(config.pageName || "")) {
        params.set("is_archive", "false");
      }

      if (currentFilters.searchTerm && config.showSearch) {
        const searchField = searchFields[config.pageName];
        if (searchField) {
          params.set(searchField, currentFilters.searchTerm);
        }
      }

      if (
        currentFilters.dateRange[0] &&
        currentFilters.dateRange[1] &&
        config.showDateFilter
      ) {
        const startDate = currentFilters.dateRange[0].toISOString().split("T")[0];
        const endDate = currentFilters.dateRange[1].toISOString().split("T")[0];

        const start = new Date(startDate);
        const end = new Date(endDate);
        const dayDifference =
          (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

        if (startDate === endDate || dayDifference <= 1) {
          params.set("created_datetime", endDate);
        } else {
          params.set("created_datetime_gte", startDate);
          params.set("created_datetime_lte", endDate);
        }
      } else if (currentFilters.dateRange[0] && config.showDateFilter) {
        params.set(
          "created_datetime_gte",
          currentFilters.dateRange[0].toISOString().split("T")[0]
        );
      } else if (currentFilters.dateRange[1] && config.showDateFilter) {
        params.set(
          "created_datetime_lte",
          currentFilters.dateRange[1].toISOString().split("T")[0]
        );
      }

      if (currentFilters.statusFilter !== null && config.showStatusFilter) {
        if (config.pageName === "subscription") {
          params.set("is_active", currentFilters.statusFilter.toString());
        } else {
          params.set(
            "lifecycle_type_stage_id",
            currentFilters.statusFilter.toString()
          );
        }
      }

      appendSortByParam(params, currentFilters.sort_by);

      if (pageSize) {
        params.set("page_size", pageSize.toString());
      }

      return `${API_BASE_URL}${endpoint}?${params.toString()}`;
    };

    const makeApiCall = async () => {
      if (onApiCall) {
        const url = generateApiUrl();
        if (url) {
          try {
            await onApiCall(url);
          } catch (error: any) {
            console.error("API call error:", error);
            if (
              error.response?.status === 401 ||
              error.response?.status === 403
            ) {
              localStorage.removeItem("authCred");
              localStorage.removeItem("authResponse");
              localStorage.removeItem("authTimestamp");
              localStorage.removeItem("selectedTenantId");
              localStorage.removeItem("selectedTenantId");
              navigate("/login");
            }
          }
        }
      }
    };

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = e.target.value;
        const newFilters = { ...filters, searchTerm: searchValue };
        setFilters(newFilters);

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        if (!searchValue) {
          onFilterChange(newFilters);
          if (onFiltersReset) {
            onFiltersReset();
          }
          setIsSearching(false);
          return;
        }

        setIsSearching(true);

        debounceTimerRef.current = setTimeout(async () => {
          if (!searchValue.trim()) {
            setIsSearching(false);
            return;
          }

          onFilterChange(newFilters);

          try {
            if (
              onApiCall &&
              config.pageName &&
              (config.pageName === "subscription" || tenantId)
            ) {
              const baseEndpoints = {
                company: "/company/",
                contact: "/contact/",
                deal: "/deal/",
                lead: "/lead/",
                pipeline: "/pipeline/",
                subscription: "/tenant-subscription/",
                proposal: "/proposal/",
                contract: "/contract/",
              };

              const searchFields = {
                company: "company_name",
                contact: "contact_full_name",
                deal: "title",
                lead: "title",
                pipeline: "title",
                subscription: "tenant",
                proposal: "subject",
                contract: "subject",
              };

              const endpoint = baseEndpoints[config.pageName];
              if (endpoint) {
                const params = new URLSearchParams();
                if (config.pageName !== "subscription" && tenantId) {
                  params.set("tenant_id", tenantId);
                }

                if (
                  ["lead", "pipeline", "deal"].includes(config.pageName || "")
                ) {
                  params.set("is_archive", "false");
                }

                params.set("page_size", "12");

                const searchField = searchFields[config.pageName];
                if (searchField) {
                  params.set(searchField, searchValue);
                }

                if (
                  filters.dateRange[0] &&
                  filters.dateRange[1] &&
                  config.showDateFilter
                ) {
                  const startDate = filters.dateRange[0]
                    .toISOString()
                    .split("T")[0];
                  const endDate = filters.dateRange[1]
                    .toISOString()
                    .split("T")[0];

                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  const dayDifference =
                    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

                  if (startDate === endDate || dayDifference <= 1) {
                    params.set("created_datetime", endDate);
                  } else {
                    params.set("created_datetime_gte", startDate);
                    params.set("created_datetime_lte", endDate);
                  }
                } else if (filters.dateRange[0] && config.showDateFilter) {
                  params.set(
                    "created_datetime_gte",
                    filters.dateRange[0].toISOString().split("T")[0]
                  );
                } else if (filters.dateRange[1] && config.showDateFilter) {
                  params.set(
                    "created_datetime_lte",
                    filters.dateRange[1].toISOString().split("T")[0]
                  );
                }

                if (filters.statusFilter !== null && config.showStatusFilter) {
                  if (config.pageName === "subscription") {
                    params.set("is_active", filters.statusFilter.toString());
                  } else {
                    params.set(
                      "lifecycle_type_stage_id",
                      filters.statusFilter.toString()
                    );
                  }
                }

                appendSortByParam(params, filters.sort_by);

                const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
                await onApiCall(url);
              }
            }
          } catch (error: any) {
            console.error("Search API error:", error);
            if (
              error.response?.status === 401 ||
              error.response?.status === 403
            ) {
              localStorage.removeItem("authCred");
              localStorage.removeItem("authResponse");
              localStorage.removeItem("authTimestamp");
              localStorage.removeItem("selectedTenantId");
              localStorage.removeItem("selectedTenantId");
              navigate("/login");
            }
          } finally {
            setIsSearching(false);
          }
        }, 500);
      },
      [
        filters,
        onFilterChange,
        onApiCall,
        onFiltersReset,
        config.pageName,
        config.showDateFilter,
        tenantId,
      ]
    );

    const handleDateRangeChange = async (
      start: Date | null,
      end: Date | null,
      rangeLabel?: string
    ) => {
      if (rangeLabel) {
        setSelectedRangeLabel(rangeLabel);
      } else if (start === null && end === null) {
        setSelectedRangeLabel(null);
      }

      const newFilters = {
        ...filters,
        dateRange: [start, end] as [Date | null, Date | null],
      };
      setFilters(newFilters);
      onFilterChange(newFilters);

      if (onApiFilterChange && config.dateField) {
        const apiFilters: Record<string, any> = {};
        if (start) {
          apiFilters[`${config.dateField}_gte`] = start
            .toISOString()
            .split("T")[0];
        }
        if (end) {
          apiFilters[`${config.dateField}_lte`] = end
            .toISOString()
            .split("T")[0];
        }
        onApiFilterChange(apiFilters);
      }

      if (
        onApiCall &&
        config.pageName &&
        (config.pageName === "subscription" || tenantId)
      ) {
        const updatedFilters = {
          ...filters,
          dateRange: [start, end] as [Date | null, Date | null],
        };
        const params = new URLSearchParams();
        if (config.pageName !== "subscription" && tenantId) {
          params.set("tenant_id", tenantId);
        }

        if (["lead", "pipeline", "deal"].includes(config.pageName || "")) {
          params.set("is_archive", "false");
        }

        params.set("page_size", "12");

        if (updatedFilters.searchTerm && config.showSearch) {
          const searchFields = {
            company: "company_name",
            contact: "contact_full_name",
            deal: "title",
            lead: "title",
            pipeline: "title",
            subscription: "tenant",
            proposal: "subject",
            contract: "subject",
          };
          const searchField = searchFields[config.pageName];
          if (searchField) {
            params.set(searchField, updatedFilters.searchTerm);
          }
        }

        if (start && end) {
          const startDate = start.toISOString().split("T")[0];
          const endDate = end.toISOString().split("T")[0];

          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          const dayDifference =
            (endDateObj.getTime() - startDateObj.getTime()) /
            (1000 * 60 * 60 * 24);

          if (startDate === endDate || dayDifference <= 1) {
            params.set("created_datetime", endDate);
          } else {
            params.set("created_datetime_gte", startDate);
            params.set("created_datetime_lte", endDate);
          }
        } else if (start) {
          params.set("created_datetime_gte", start.toISOString().split("T")[0]);
        } else if (end) {
          params.set("created_datetime_lte", end.toISOString().split("T")[0]);
        }

        if (updatedFilters.statusFilter !== null && config.showStatusFilter) {
          if (config.pageName === "subscription") {
            params.set("is_active", updatedFilters.statusFilter.toString());
          } else {
            params.set(
              "lifecycle_type_stage_id",
              updatedFilters.statusFilter.toString()
            );
          }
        }

        appendSortByParam(params, updatedFilters.sort_by);

        const baseEndpoints = {
          company: "/company/",
          contact: "/contact/",
          deal: "/deal/",
          lead: "/lead/",
          pipeline: "/pipeline/",
          subscription: "/tenant-subscription/",
          proposal: "/proposal/",
          contract: "/contract/",
        };

        const endpoint = baseEndpoints[config.pageName];
        if (endpoint) {
          const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
          try {
            await onApiCall(url);
          } catch (error) {
            handleApiError(error, "Date range");
          }
        }
      }
    };

    const handleStatusChange = async (stageId: number | boolean | null) => {
      if (statusDropdownRef.current) {
        const dropdownButton = statusDropdownRef.current.querySelector(
          '[data-bs-toggle="dropdown"]'
        ) as HTMLElement;
        if (dropdownButton) {
          if ((window as any).bootstrap) {
            const dropdown = new (window as any).bootstrap.Dropdown(
              dropdownButton
            );
            dropdown.hide();
          } else {
            const dropdownMenu = statusDropdownRef.current.querySelector(
              ".dropdown-menu"
            ) as HTMLElement;
            if (dropdownMenu) {
              dropdownButton.classList.remove("show");
              dropdownMenu.classList.remove("show");
              dropdownButton.setAttribute("aria-expanded", "false");
            }
          }
        }
      }

      const newFilters = {
        ...filters,
        statusFilter: stageId,
      };
      setFilters(newFilters);
      onFilterChange(newFilters);

      if (stageId === null) {
        if (onFiltersReset) {
          onFiltersReset();
        }
        return;
      }

      if (onApiFilterChange && config.showStatusFilter) {
        const apiFilters: Record<string, any> = {};
        if (stageId !== null) {
          if (config.pageName === "subscription") {
            apiFilters.is_active = stageId;
          } else {
            apiFilters.lifecycle_type_stage_id = stageId;
          }
        }
        onApiFilterChange(apiFilters);
      }

      if (
        onApiCall &&
        config.pageName &&
        (config.pageName === "subscription" || tenantId)
      ) {
        const updatedFilters = {
          ...filters,
          statusFilter: stageId,
        };
        const params = new URLSearchParams();
        if (config.pageName !== "subscription" && tenantId) {
          params.set("tenant_id", tenantId);
        }

        if (["lead", "pipeline", "deal"].includes(config.pageName || "")) {
          params.set("is_archive", "false");
        }

        params.set("page_size", "12");

        if (updatedFilters.searchTerm && config.showSearch) {
          const searchFields = {
            company: "company_name",
            contact: "contact_full_name",
            deal: "title",
            lead: "title",
            pipeline: "title",
            subscription: "tenant",
            proposal: "subject",
            contract: "subject",
          };
          const searchField = searchFields[config.pageName];
          if (searchField) {
            params.set(searchField, updatedFilters.searchTerm);
          }
        }

        if (
          updatedFilters.dateRange[0] &&
          updatedFilters.dateRange[1] &&
          config.showDateFilter
        ) {
          const startDate = updatedFilters.dateRange[0]
            .toISOString()
            .split("T")[0];
          const endDate = updatedFilters.dateRange[1]
            .toISOString()
            .split("T")[0];

          const start = new Date(startDate);
          const end = new Date(endDate);
          const dayDifference =
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

          if (startDate === endDate || dayDifference <= 1) {
            params.set("created_datetime", endDate);
          } else {
            params.set("created_datetime_gte", startDate);
            params.set("created_datetime_lte", endDate);
          }
        } else if (updatedFilters.dateRange[0] && config.showDateFilter) {
          params.set(
            "created_datetime_gte",
            updatedFilters.dateRange[0].toISOString().split("T")[0]
          );
        } else if (updatedFilters.dateRange[1] && config.showDateFilter) {
          params.set(
            "created_datetime_lte",
            updatedFilters.dateRange[1].toISOString().split("T")[0]
          );
        }

        if (stageId !== null && config.showStatusFilter) {
          if (config.pageName === "subscription") {
            params.set("is_active", stageId.toString());
          } else {
            params.set("lifecycle_type_stage_id", stageId.toString());
          }
        }

        appendSortByParam(params, updatedFilters.sort_by);

        const baseEndpoints = {
          company: "/company/",
          contact: "/contact/",
          deal: "/deal/",
          lead: "/lead/",
          pipeline: "/pipeline/",
          subscription: "/tenant-subscription/",
          proposal: "/proposal/",
          contract: "/contract/",
        };

        const endpoint = baseEndpoints[config.pageName];
        if (endpoint) {
          const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
          try {
            await onApiCall(url);
          } catch (error) {
            handleApiError(error, "Status filter");
          }
        }
      }
    };

    const resetFilters = async () => {
      const defaultFilters: FilterState = {
        searchTerm: "",
        dateRange: [null, null] as [Date | null, Date | null],
        industryFilter: [],
        countryFilter: [],
        statusFilter: null,
        sortBy: config.defaultSort?.field || "",
        sortOrder: config.defaultSort?.order || "desc",
        sort_by: undefined,
      };
      setFilters(defaultFilters);
      setSelectedRangeLabel(null);
      onFilterChange(defaultFilters);

      if (config.pageName) {
        const { clearFilterState } = await import(
          "../../utils/filterStateUtils"
        );
        clearFilterState(config.pageName);
      }

      if (onApiFilterChange) {
        onApiFilterChange({});
      }

      if (onFiltersReset) {
        onFiltersReset();
      }

      if (
        onApiCall &&
        config.pageName &&
        (config.pageName === "subscription" || tenantId)
      ) {
        const params = new URLSearchParams();
        if (config.pageName !== "subscription" && tenantId) {
          params.set("tenant_id", tenantId);
        }

        if (["lead", "pipeline", "deal"].includes(config.pageName || "")) {
          params.set("is_archive", "false");
        }

        params.set("page_size", "12");

        const baseEndpoints = {
          company: "/company/",
          contact: "/contact/",
          deal: "/deal/",
          lead: "/lead/",
          pipeline: "/pipeline/",
          subscription: "/tenant-subscription/",
          proposal: "/proposal/",
          contract: "/contract/",
        };

        const endpoint = baseEndpoints[config.pageName];
        if (endpoint) {
          const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
          try {
            await onApiCall(url);
          } catch (error) {
            handleApiError(error, "Reset filters");
          }
        }
      }
    };

    const handleSortByChange = async (sortBy: SortByOption | null) => {
      const newFilters: FilterState = {
        ...filters,
        sort_by: sortBy ?? undefined,
        sortBy: "created_datetime",
        sortOrder: sortBy === "oldest" ? "asc" : "desc",
      };
      setFilters(newFilters);
      onFilterChange(newFilters);

      if (
        onApiCall &&
        config.pageName &&
        (config.pageName === "subscription" || tenantId)
      ) {
        const url = generateApiUrl(newFilters, 12);
        if (url) {
          try {
            await onApiCall(url);
          } catch (error) {
            handleApiError(error, "Sort");
          }
        }
      }
    };

    useEffect(() => {
      if ((sortBy ?? undefined) === filters.sort_by) return;

      if (sortBy === undefined) {
        setFilters((prev) => ({ ...prev, sort_by: undefined }));
      } else {
        handleSortByChange(sortBy);
      }
    }, [sortBy]);

    useEffect(() => {
      if (searchTerm === undefined || searchTerm === filters.searchTerm) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setIsSearching(false);
      setFilters((prev) => ({ ...prev, searchTerm }));
    }, [searchTerm]);

    const dateRangeKey = dateRange
      ? `${dateRange[0]?.getTime() ?? ""}|${dateRange[1]?.getTime() ?? ""}`
      : undefined;

    useEffect(() => {
      if (!dateRange) return;
      const currentKey = `${filters.dateRange[0]?.getTime() ?? ""}|${filters.dateRange[1]?.getTime() ?? ""}`;
      if (dateRangeKey === currentKey) return;
      setFilters((prev) => ({ ...prev, dateRange: [dateRange[0], dateRange[1]] }));
      if (!dateRange[0] && !dateRange[1]) {
        setSelectedRangeLabel(null);
        if (config.pageName) {
          clearFilterState(config.pageName);
        }
      }
    }, [dateRangeKey]);

    const getSearchPlaceholder = () => {
      if (!config.searchPlaceholder) {
        switch (config.pageName) {
          case "lead":
            return `Search ${displayNames.Lead}...`;
          case "pipeline":
            return `Search ${displayNames.Pipeline}...`;
          case "deal":
            return `Search ${displayNames.Deal}...`;
          case "company":
            return "Search Company...";
          case "contact":
            return "Search Contact...";
          case "subscription":
            return "Search Subscription...";
          case "proposal":
            return `Search ${displayNames.Proposal}...`;
          case "contract":
            return `Search ${displayNames.Contract}...`;
          default:
            return "Search...";
        }
      }
      return config.searchPlaceholder;
    };

    const getStatusPlaceholder = () => {
      switch (config.pageName) {
        case "lead":
          return `Select ${displayNames.Lead} Status`;
        case "pipeline":
          return `Select ${displayNames.Pipeline} Status`;
        case "deal":
          return `Select ${displayNames.Deal} Status`;
        case "subscription":
          return "Select Subscription Status";
        default:
          return "Select Status";
      }
    };

    return (
      <div className={className}>
        {/* Filter Controls */}
        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
          <div className="d-flex align-items-center flex-wrap row-gap-2">
            {/* Search Filter */}
            {config.showSearch && (
              <div
                className={`icon-form me-3 ${
                  filters.searchTerm ? "filter-active" : ""
                }`}
              >
                <span className="form-icon">
                  <i
                    className={`ti ${
                      isSearching ? "ti-loader-2 spin" : "ti-search"
                    }`}
                  />
                </span>
                <input
                  type="text"
                  className={`form-control ${
                    filters.searchTerm ? "filter-active-input" : ""
                  }`}
                  placeholder={getSearchPlaceholder()}
                  value={filters.searchTerm ?? ""}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            )}

            {/* Date Range Picker */}
            {config.showDateFilter && (
              <div
                className={`icon-form me-3 ${
                  filters.dateRange[0] || filters.dateRange[1]
                    ? "filter-active"
                    : ""
                }`}
              >
                <span className="form-icon">
                  <i className="ti ti-calendar" />
                </span>
                <DateRangePicker
                  initialSettings={{
                    ...initialSettings,
                    autoUpdateInput: false,
                  }}
                  onApply={(event, picker) => {
                    const chosenLabel = picker.chosenLabel || undefined;
                    handleDateRangeChange(
                      picker.startDate.toDate(),
                      picker.endDate.toDate(),
                      chosenLabel
                    );
                  }}
                  onCancel={() => {
                    handleDateRangeChange(null, null);
                  }}
                >
                  <input
                    className={`form-control bookingrange table-filters-daterange ${
                      filters.dateRange[0] || filters.dateRange[1]
                        ? "filter-active-input"
                        : ""
                    }`}
                    type="text"
                    placeholder="Select date range"
                    value={
                      filters.dateRange[0] && filters.dateRange[1]
                        ? `${filters.dateRange[0].toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })} - ${filters.dateRange[1].toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`
                        : ""
                    }
                    readOnly
                    onChange={() => {}}
                    style={{
                      paddingLeft: "34px",
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      border:
                        filters.dateRange[0] || filters.dateRange[1]
                          ? undefined
                          : "1px solid #e0e0e0",
                    }}
                  />
                </DateRangePicker>
              </div>
            )}

            {/* Status Filter */}
            {config.showStatusFilter && (
              <div
                className="btn-group dropdown-action me-3"
                ref={statusDropdownRef}
              >
                <button
                  type="button"
                  className={`btn dropdown-toggle ${
                    filters.statusFilter !== null ? "btn-primary" : "btn-white"
                  }`}
                  data-bs-toggle="dropdown"
                  disabled={
                    isLoadingStages && config.pageName !== "subscription"
                  }
                  style={{
                    minWidth: "200px",
                    textAlign: "left" as const,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "14px",
                    fontWeight: "400",
                    border:
                      filters.statusFilter !== null
                        ? undefined
                        : "1px solid #e0e0e0",
                    backgroundColor:
                      filters.statusFilter !== null ? undefined : "#ffffff",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className={`ti ${
                        isLoadingStages && config.pageName !== "subscription"
                          ? "ti-loader-2 spin"
                          : "ti-filter"
                      } me-2`}
                    />
                    {filters.statusFilter !== null
                      ? config.pageName === "subscription"
                        ? filters.statusFilter === true
                          ? "Active"
                          : "Inactive"
                        : lifecycleStages.find(
                            (stage) =>
                              stage.lifecycle_type_stage_id ===
                              filters.statusFilter
                          )?.stage_name || getStatusPlaceholder()
                      : getStatusPlaceholder()}
                  </span>
                </button>
                <div className="dropdown-menu" style={{ minWidth: "200px" }}>
                  <button
                    type="button"
                    className={`dropdown-item ${
                      filters.statusFilter === null
                        ? "text-primary fw-medium"
                        : ""
                    }`}
                    onClick={() => {
                      handleStatusChange(null);
                    }}
                    style={{
                      textAlign: "left",
                      border: "none",
                      background: "none",
                      width: "100%",
                    }}
                  >
                    All Statuses
                  </button>
                  <div className="dropdown-divider" />
                  {config.pageName === "subscription" ? (
                    <>
                      <button
                        type="button"
                        className={`dropdown-item ${
                          filters.statusFilter === true
                            ? "text-primary fw-medium"
                            : ""
                        }`}
                        onClick={() => {
                          handleStatusChange(true);
                        }}
                        style={{
                          textAlign: "left",
                          border: "none",
                          background: "none",
                          width: "100%",
                        }}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        className={`dropdown-item ${
                          filters.statusFilter === false
                            ? "text-primary fw-medium"
                            : ""
                        }`}
                        onClick={() => {
                          handleStatusChange(false);
                        }}
                        style={{
                          textAlign: "left",
                          border: "none",
                          background: "none",
                          width: "100%",
                        }}
                      >
                        Inactive
                      </button>
                    </>
                  ) : (
                    lifecycleStages.map((stage) => (
                      <button
                        key={stage.lifecycle_type_stage_id}
                        type="button"
                        className={`dropdown-item ${
                          filters.statusFilter === stage.lifecycle_type_stage_id
                            ? "text-primary fw-medium"
                            : ""
                        }`}
                        onClick={() => {
                          handleStatusChange(stage.lifecycle_type_stage_id);
                        }}
                        style={{
                          textAlign: "left",
                          border: "none",
                          background: "none",
                          width: "100%",
                        }}
                      >
                        {stage.stage_name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Clear Filter Button - appears when any filter is active */}
          {!hideClearButton &&
            (filters.searchTerm ||
            filters.dateRange[0] ||
            filters.dateRange[1] ||
            filters.statusFilter !== null) && (
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={resetFilters}
                style={{
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                <i className="ti ti-x me-1" />
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

TableFilters.displayName = "TableFilters";

export default TableFilters;
